"use client";

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useHydration } from '@/hooks/useHydration';
import { makeAuthenticatedRequest } from '@/utils/auth';

const UserContext = createContext();

export function UserProvider({ children }) {
  const isHydrated = useHydration();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!isHydrated) return;
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      const res = await makeAuthenticatedRequest(`${process.env.NEXT_PUBLIC_API_END_POINT}/auth/getUserLocal`);
      
      if (!res) {
        setUser(null);
        setLoading(false);
        return; // Request was handled by auth utility (token expired)
      }

      const data = await res.json();

      if (data.success) {
        setUser(data.user);
        // Update localStorage with fresh user data
        localStorage.setItem("user", JSON.stringify(data.user));
      } else {
        setUser(null);
        // Clear invalid token
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        
        // Redirect to login if account deactivated
        if (data.message === "Account deactivated") {
          window.location.href = "/auth/signin";
        }
      }
    } catch (error) {
      setUser(null); // Clear stale user data on error
      // Clear localStorage if API fails
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } finally {
      setLoading(false);
    }
  }, [isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    
    fetchUser();

    const handleProfileUpdate = () => {
      fetchUser();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [fetchUser, isHydrated]);

  return (
    <UserContext.Provider value={{ user, loading, refetch: fetchUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useCurrentUser() {
  const context = useContext(UserContext);
  if (!context) {
    // Return default values when used outside provider (e.g., during build)
    return { user: null, loading: true, refetch: () => {} };
  }
  return context;
}
